// src/pages/qc/CcDetailModal.jsx
import { useState } from "react";
import { ImageUpload } from "../../components/ImageUpload";
import { todayDubai } from "../../utils/dateUtils";

const TODAY = todayDubai();

const TYPE_LABELS = { WB: "Water Based", BF: "Brake Fluid", COOLANT: "Coolant", HM: "Hot Melt" };

const STATUS_STYLE = {
  OPEN:      { bg: "#fef3c7", color: "#92400e" },
  RESPONDED: { bg: "#dbeafe", color: "#1e40af" },
  CLOSED:    { bg: "#d1fae5", color: "#065f46" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] ?? { bg: "#f3f4f6", color: "#374151" };
  const label = status === "OPEN" ? "Open" : status === "RESPONDED" ? "Responded" : status === "CLOSED" ? "Closed" : status;
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4,
      padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
      {label}
    </span>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13 }}>{value || "—"}</div>
    </div>
  );
}

function TwoCol({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>{children}</div>;
}

function TextBlock({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb",
        padding: "8px 12px", borderRadius: 4, fontSize: 13, whiteSpace: "pre-wrap" }}>
        {value}
      </div>
    </div>
  );
}

// ── Investigation response sub-form ───────────────────────────────────────────
function RespondTab({ cc, onRespond, onSaved, canEdit }) {
  const [form, setForm] = useState({
    rootCause:        cc.rootCause        || "",
    correctiveAction: cc.correctiveAction || "",
    implDate:         cc.implDate         || "",
    implPerson:       cc.implPerson       || "",
    implSignature:    cc.implSignature    || "",
    respDate:         cc.respDate         || TODAY,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  if (cc.status === "CLOSED" || (!canEdit && cc.status === "RESPONDED")) {
    return (
      <div>
        <TextBlock label="(1) Root Cause" value={cc.rootCause} />
        <TextBlock label="(2) Action to Prevent Recurrence" value={cc.correctiveAction} />
        <TwoCol>
          <Field label="(3) Date of Implementation" value={cc.implDate} />
          <Field label="Person Responsible"          value={cc.implPerson} />
          <Field label="Signature"                   value={cc.implSignature} />
          <Field label="Response Date"               value={cc.respDate} />
        </TwoCol>
      </div>
    );
  }

  if (!canEdit) return <div style={{ color: "#aaa", fontSize: 12 }}>Not responded yet.</div>;

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onRespond({
        ...form,
        implDate: form.implDate || null,
        respDate: form.respDate || null,
      });
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSave}>
      <div className="form-group">
        <label className="form-label">(1) Results of Investigation / Root Cause</label>
        <textarea className="form-input" rows={6} value={form.rootCause}
          onChange={e => set("rootCause", e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">(2) Action to Prevent Recurrence</label>
        <textarea className="form-input" rows={6} value={form.correctiveAction}
          onChange={e => set("correctiveAction", e.target.value)} />
      </div>
      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">(3) Date of Implementation</label>
          <input className="form-input" type="date" value={form.implDate}
            onChange={e => set("implDate", e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Person Responsible</label>
          <input className="form-input" value={form.implPerson}
            onChange={e => set("implPerson", e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Signature</label>
          <input className="form-input" value={form.implSignature}
            onChange={e => set("implSignature", e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Response Date</label>
          <input className="form-input" type="date" value={form.respDate}
            onChange={e => set("respDate", e.target.value)} />
        </div>
      </div>
      {error && <div className="alert-warn">{error}</div>}
      <div style={{ marginTop: 12, textAlign: "right" }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Saving…" : "Save Response"}
        </button>
      </div>
    </form>
  );
}

// ── Main detail modal ─────────────────────────────────────────────────────────
export function CcDetailModal({ cc, onRespond, onClose, onCancel, canEdit }) {
  const [tab, setTab] = useState("details");
  const [closing, setClosing] = useState(false);
  const canEditRecord = canEdit && cc.status !== "CLOSED";

  const tabs = [
    { id: "details",  label: "Complaint" },
    { id: "response", label: "Investigation & Action" },
    { id: "images",   label: "Attachments" },
  ];

  async function handleClose() {
    if (!window.confirm(`Close complaint ${cc.ccNumber}? This cannot be undone.`)) return;
    setClosing(true);
    try { await onClose(); }
    catch { setClosing(false); }
  }

  return (
    <div className="modal" style={{ width: 640, maxHeight: "90vh", overflowY: "auto" }}>
      <div className="modal-header">
        <div>
          <div className="modal-title">{cc.ccNumber}</div>
          <StatusBadge status={cc.status} />
          {cc.productType && (
            <span style={{ marginLeft: 8, fontSize: 11, color: "#6b7280" }}>
              {TYPE_LABELS[cc.productType] ?? cc.productType}
            </span>
          )}
        </div>
        <button className="modal-close" onClick={onCancel}>✕</button>
      </div>

      <div style={{ display: "flex", borderBottom: "2px solid #e5e7eb", padding: "0 16px" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "8px 14px", fontSize: 12, fontWeight: 600,
              color: tab === t.id ? "#1a3a6b" : "#6b7280",
              borderBottom: tab === t.id ? "3px solid #1a3a6b" : "3px solid transparent",
              marginBottom: -2,
            }}>
            {t.label}
          </button>
        ))}
        {canEdit && cc.status !== "CLOSED" && (
          <button onClick={handleClose} disabled={closing}
            style={{ marginLeft: "auto", background: "none", border: "none",
              cursor: "pointer", fontSize: 12, color: "#6b7280", padding: "8px 14px" }}>
            {closing ? "Closing…" : "Mark Closed"}
          </button>
        )}
      </div>

      <div className="modal-body">
        {tab === "details" && (
          <div>
            <TwoCol>
              <Field label="Customer Name"  value={cc.customerName} />
              <Field label="Complaint Date" value={cc.complaintDate} />
              <Field label="Product"        value={cc.productName || cc.productId} />
              <Field label="Batch Number"   value={cc.batchNumber} />
              <Field label="Reported By"    value={cc.reportedBy} />
            </TwoCol>
            <TextBlock label="Complaint"                        value={cc.complaint} />
            <TextBlock label="Findings (including immediate causes)" value={cc.findings} />
          </div>
        )}

        {tab === "response" && (
          <RespondTab cc={cc} onRespond={onRespond} onSaved={() => setTab("details")} canEdit={canEditRecord} />
        )}

        {tab === "images" && (
          <ImageUpload entityType="CC" entityId={cc.id} canEdit={canEditRecord} />
        )}
      </div>
    </div>
  );
}
